import React, { useEffect, useState, useRef } from 'react';
declare global {
  interface Window {
    gapi: any;
    renderOptIn: () => void;
  }
}
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, ArrowLeft, Receipt, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PaymentStatus } from '@/components/PaymentStatus';
import MetaTags from '@/components/MetaTags';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { getStatusLabel, getStatusDescription } from '@/lib/statusMapping';
import { trackAdPurchase } from '@/lib/analytics';

// Helper to trigger backend active verification
// Helper to trigger backend active verification
const triggerBackendVerification = async (orderId: string) => {
  try {
    console.log('[PaymentSuccess] Triggering backend verification for order:', orderId);

    // Check session first to avoid 401 error on payment_transactions lookup for Guests
    const { data: { session } } = await supabase.auth.getSession();

    let transactions = null;

    if (session) {
      // Only verify via DB if we have a user, otherwise we know RLS will block it
      const { data } = await supabase
        .from('payment_transactions')
        .select('id, status')
        .eq('order_id', orderId)
        .order('created_at', { ascending: false })
        .limit(1);
      transactions = data;
    }

    if (transactions && transactions.length > 0) {
      const transaction = transactions[0];
      console.log(`[PaymentSuccess] Found transaction ${transaction.id} (${transaction.status}). Calling complete-payment...`);
      await supabase.functions.invoke('complete-payment', {
        body: {
          transaction_id: transaction.id,
          payment_status: 'completed'
        }
      });
    } else {
      // Fallback for Guests (RLS hides transactions)
      // We send the order_id and let the backend (Service Role) find the transaction.
      console.log('[PaymentSuccess] No transaction visible (likely Guest/RLS). Calling complete-payment with order_id fallback to force reconciliation...');
      await supabase.functions.invoke('complete-payment', {
        body: {
          order_id: orderId, // New supported param
          payment_status: 'completed'
        }
      });
    }

  } catch (error) {
    console.error('[PaymentSuccess] Error triggering backend verification:', error);
  }
};

const PaymentSuccess = () => {
  const { user } = useAuth();
  const { clearCart } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const transactionId = searchParams.get('transaction_id');
  const urlOrderId = searchParams.get('order_id') || searchParams.get('order') || searchParams.get('orderIdClientUsage') || searchParams.get('orderId') || searchParams.get('oid');
  const status = searchParams.get('status') || (searchParams.get('success') === 'true' ? 'success' : null);
  const isMock = searchParams.get('mock') === 'true';
  const source = searchParams.get('source');

  // Try to get orderId from URL or sessionStorage (fallback for Invoice4U)
  const getOrderId = (): string | null => {
    if (urlOrderId) return urlOrderId;

    // Fallback: check sessionStorage
    const storedOrderId = sessionStorage.getItem('pending_order_id');
    if (storedOrderId) {
      console.log('Using orderId from sessionStorage:', storedOrderId);
      return storedOrderId;
    }

    return null;
  };

  const orderId = getOrderId();

  const [isProcessing, setIsProcessing] = useState(false);
  const [orderStatus, setOrderStatus] = useState<string | null>(null);
  const [orderData, setOrderData] = useState<any>(null);
  const pollAttemptsRef = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [cartCleared, setCartCleared] = useState(false);
  const [fallbackSuccess, setFallbackSuccess] = useState(false);

  // Maximum polling attempts (5 minutes with 5-second intervals)
  const MAX_POLL_ATTEMPTS = 60;

  const formatOrderNumber = (orderNumber: string | null) => {
    if (!orderNumber) return 'לא זמין';
    const digits = orderNumber.replace(/[^0-9]/g, '');
    if (digits.length === 9) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 9)}`;
    }
    return orderNumber;
  };

  const fetchOrderStatus = async (orderIdToCheck: string) => {
    try {
      console.log('Fetching order status for:', orderIdToCheck);

      const { data: order, error: orderError } = await supabase
        .from('orders')
        .select('id, order_number, status, total_amount, created_at, updated_at')
        .eq('id', orderIdToCheck)
        .maybeSingle();

      let finalOrder = order;

      // If RLS blocked access (no user session) or order not found, try the guest function
      if (orderError || !order) {
        console.log('Order not found via direct select (likely Guest RLS). Trying get-guest-order function...');
        
        // Try to get email from URL params or sessionStorage
        const emailParam = searchParams.get('email');
        const storedEmail = sessionStorage.getItem('checkout_email') || sessionStorage.getItem('guest_email');
        const email = emailParam || storedEmail || user?.email;
        
        if (!email) {
          console.warn('No email available for guest order verification. Using fallback success.');
          setFallbackSuccess(true);
          return 'processing';
        }
        
        const { data: guestOrder, error: fnError } = await supabase.functions.invoke('get-guest-order', {
          body: { orderId: orderIdToCheck, email }
        });

        if (fnError || !guestOrder) {
          console.warn('Failed to fetch order details (DB + Function). using fallback success.');
          setFallbackSuccess(true);
          // Do strictly confirm successful payment? 
          // If we are here, we are polling. 
          // If Invoice4U sent us here, we are OK.
          return 'processing';
        } else {
          finalOrder = guestOrder;
        }
      }

      if (finalOrder) {
        console.log('Order found, ID:', finalOrder.id);
        setOrderData(finalOrder);
        setOrderStatus(finalOrder.status);

        // Clear cart and track purchase when we confirm payment was successful
        if ((finalOrder.status === 'processing') && !cartCleared) {
          clearCart();
          setCartCleared(true);
          
          // Track ad pixel purchase (with dedupe via transaction_id)
          const trackedPurchases = sessionStorage.getItem('tracked_ad_purchases') || '[]';
          const purchases = JSON.parse(trackedPurchases);
          
          if (!purchases.includes(finalOrder.id)) {
            trackAdPurchase({
              transaction_id: finalOrder.id,
              value: finalOrder.total_amount || 0,
              currency: 'ILS',
              contents: []
            });
            
            purchases.push(finalOrder.id);
            sessionStorage.setItem('tracked_ad_purchases', JSON.stringify(purchases));
          }
        }

        return finalOrder.status;
      }

      return null;
    } catch (err) {
      console.error('Error fetching order status:', err);
      // Fallback to success on error if we have an order ID
      setFallbackSuccess(true);
      return 'processing';
    }
  };

  // clearCart is now imported from useCart

  const startPolling = (orderIdToPoll: string) => {
    console.log('Starting order status polling for:', orderIdToPoll);
    setIsProcessing(true);
    pollAttemptsRef.current = 0;

    // Trigger backend verification immediately to catch missing webhooks
    triggerBackendVerification(orderIdToPoll);

    const pollInterval = setInterval(async () => {
      pollAttemptsRef.current++;
      const attempts = pollAttemptsRef.current;

      const currentStatus = await fetchOrderStatus(orderIdToPoll);

      if ((currentStatus !== 'pending' && currentStatus !== null) || attempts >= MAX_POLL_ATTEMPTS) {
        console.log('Stopping polling. Status:', currentStatus, 'Attempts:', attempts);
        clearInterval(pollInterval);
        setIsProcessing(false);

        // If timing out but we have a fallback or it's invoice4u, assume success
        if (attempts >= MAX_POLL_ATTEMPTS && currentStatus === 'pending') {
          // Don't show error, show pending/check email
          setFallbackSuccess(true);
        }
        return;
      }
    }, 5000);

    // Also fetch immediately
    fetchOrderStatus(orderIdToPoll);
  };

  const handleRefreshStatus = () => {
    if (orderId) {
      setError(null);
      startPolling(orderId);
    }
  };

  useEffect(() => {
    // CRITICAL: Check if we're in an iframe and break out to parent window
    // CRITICAL: Check if we're in an iframe and break out to parent window
    if (window.self !== window.top) {
      console.log('Payment success page detected in iframe - redirecting parent window');
      console.log('Current Iframe URL:', window.location.href); // Log for debugging

      const currentOrigin = window.location.origin;
      // Preserve ALL query parameters from the iframe URL
      const currentSearch = window.location.search;

      const successUrl = `${currentOrigin}/payment/success${currentSearch}`;

      try {
        window.top!.location.href = successUrl;
        return;
      } catch (error) {
        console.error('Failed to redirect parent window:', error);
        window.parent.postMessage({
          type: 'PAYMENT_SUCCESS_REDIRECT',
          url: successUrl
        }, '*');
      }
    }

    // Store orderId in sessionStorage when available
    if (orderId) {
      sessionStorage.setItem('pending_order_id', orderId);
    }

    // Handle different scenarios
    if (orderId) {
      console.log('Handling order-based payment for order:', orderId);
      if (source === 'invoice4u' || (!transactionId && orderId)) {
        console.log('Starting polling for order status updates');
        startPolling(orderId);
      } else {
        console.log('Fetching order status once');
        fetchOrderStatus(orderId);
      }
    } else if (isMock && status === 'success' && transactionId) {
      setError('לא נמצא מזהה הזמנה. אנא צור קשר לתמיכה.');
    }
  }, [orderId, source, isMock, status, transactionId]);

  useEffect(() => {
    // Inject Google Customer Reviews script
    if (orderData && !error) {
      const script = document.createElement('script');
      script.src = "https://apis.google.com/js/platform.js?onload=renderOptIn";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);

      (window as any).renderOptIn = function () {
        if ((window as any).gapi) {
          (window as any).gapi.load('surveyoptin', function () {
            // Calculate estimated delivery date (e.g., 7 days from now)
            const deliveryDate = new Date();
            deliveryDate.setDate(deliveryDate.getDate() + 7);
            const dateStr = deliveryDate.toISOString().split('T')[0];

            (window as any).gapi.surveyoptin.render({
              "merchant_id": 5721242591,
              "order_id": orderData.order_number || orderData.id,
              "email": orderData.billing_address?.email || user?.email || "",
              "delivery_country": "IL",
              "estimated_delivery_date": dateStr,
            });
          });
        }
      };

      return () => {
        document.body.removeChild(script);
        delete (window as any).renderOptIn;
      };
    }
  }, [orderData, error, user]);

  const handleMockPaymentCompletion = async () => {
    try {
      if (!transactionId) return;

      const { error } = await supabase
        .from('payment_transactions')
        .update({ status: 'completed' })
        .eq('id', transactionId);

      if (error) throw error;

      // Update local status to reflect success
      setOrderStatus('completed');
      setFallbackSuccess(true);

    } catch (err) {
      console.error('Mock completion failed:', err);
    }
  };

  // Determine what to display based on order status
  const getDisplayStatus = () => {
    // Fallback success always wins if enabled
    if (fallbackSuccess) {
      return {
        icon: <CheckCircle className="h-16 w-16 text-success" />,
        title: 'תשלום התקבל בהצלחה!',
        description: 'ההזמנה נקלטה במערכת. אישור הזמנה וקבלה יישלחו למייל שלך בדקות הקרובות.',
        variant: 'success' as const
      };
    }

    if (source === 'invoice4u' && orderId) {
      // ... same as before

      // Only show cancelled if explicitly cancelled
      if (orderStatus === 'cancelled') {
        return {
          icon: <AlertCircle className="h-16 w-16 text-destructive" />,
          title: 'התשלום בוטל',
          description: 'התשלום לא הושלם בהצלחה. ההזמנה בוטלה.',
          variant: 'cancelled' as const
        };
      }

      // For any other status (including pending/processing), show success
      // because Invoice4U only redirects here on successful payment
      return {
        icon: <CheckCircle className="h-16 w-16 text-success" />,
        title: 'התשלום הושלם בהצלחה!',
        description: 'תודה על הרכישה! אנו נטפל בהזמנה שלך בהקדם האפשרי.',
        variant: 'success' as const
      };
    }

    // If order status is processing or completed, show success
    if (orderStatus === 'processing' || orderStatus === 'completed') {
      return {
        icon: <CheckCircle className="h-16 w-16 text-success" />,
        title: 'התשלום הושלם בהצלחה!',
        description: 'תודה על הרכישה! אנו נטפל בהזמנה שלך בהקדם האפשרי.',
        variant: 'success' as const
      };
    }

    // If we have success in URL params, show success
    if (status === 'success') {
      return {
        icon: <CheckCircle className="h-16 w-16 text-success" />,
        title: 'התשלום הושלם בהצלחה!',
        description: 'תודה על הרכישה! אנו נטפל בהזמנה שלך בהקדם האפשרי.',
        variant: 'success' as const
      };
    }

    if (error) {
      return {
        icon: <AlertCircle className="h-16 w-16 text-destructive" />,
        title: 'שגיאה בטעינת ההזמנה',
        description: error,
        variant: 'error' as const
      };
    }

    if (orderStatus === 'cancelled') {
      return {
        icon: <AlertCircle className="h-16 w-16 text-destructive" />,
        title: 'התשלום בוטל',
        description: 'התשלום לא הושלם בהצלחה. ההזמנה בוטלה.',
        variant: 'cancelled' as const
      };
    }

    // Show processing state while we're polling for status update
    if ((orderStatus === 'pending' || !orderStatus) && isProcessing) {
      return {
        icon: <Loader2 className="h-16 w-16 text-primary animate-spin" />,
        title: 'מעבד תשלום...',
        description: 'התשלום עובד והמערכת מעדכנת את סטטוס ההזמנה. אנא המתן...',
        variant: 'processing' as const
      };
    }

    // If we still have pending status after polling stopped, show pending message
    if (orderStatus === 'pending' && !isProcessing) {
      return {
        icon: <AlertCircle className="h-16 w-16 text-warning" />,
        title: 'תשלום בהמתנה',
        description: 'התשלום עדיין בעיבוד. אם התשלום לא התעדכן תוך מספר דקות, אנא צור קשר לתמיכה.',
        variant: 'pending' as const
      };
    }

    // Default case
    return {
      icon: <CheckCircle className="h-16 w-16 text-success" />,
      title: 'עמוד תשלום',
      description: 'מעדכן פרטי תשלום...',
      variant: 'default' as const
    };
  };

  const displayStatus = getDisplayStatus();

  return (
    <>
      <MetaTags
        title="תשלום הושלם בהצלחה - Excess Market"
        description="התשלום שלך הושלם בהצלחה. תודה על הרכישה!"
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 py-8 pt-28 lg:pt-16">
          <div className="max-w-2xl mx-auto space-y-6">
            {/* Dynamic Status Display */}
            <Card className="text-center">
              <CardHeader>
                <div className="flex justify-center mb-4">
                  {displayStatus.icon}
                </div>
                <CardTitle className={`text-2xl ${displayStatus.variant === 'success' ? 'text-success' : displayStatus.variant === 'error' || displayStatus.variant === 'cancelled' ? 'text-destructive' : 'text-primary'}`}>
                  {displayStatus.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  {displayStatus.description}
                </p>

                {/* Error handling with refresh button */}
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="space-y-2">
                      <p>{error}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRefreshStatus}
                        className="mt-2 mr-2"
                      >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        נסה שוב
                      </Button>

                      {/* Debug Info for Support */}
                      <div className="mt-4 p-2 bg-black/10 rounded text-xs font-mono text-left" dir="ltr">
                        <p><strong>Debug Info:</strong></p>
                        <p className="break-all"><strong>URL:</strong> {window.location.href}</p>
                        <p><strong>Params:</strong> {JSON.stringify(Object.fromEntries(new URLSearchParams(window.location.search)))}</p>
                        <p><strong>Ref:</strong> {document.referrer}</p>
                        <p><strong>OID:</strong> {orderId || 'desc_missing'}</p>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                {(orderData?.id || orderId) && (
                  <div className="bg-muted/50 p-4 rounded-lg space-y-3">
                    <div className="flex items-center justify-center gap-2">
                      <Receipt className="h-5 w-5 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">מספר הזמנה:</span>
                      <span className="font-mono font-bold text-lg">
                        #{formatOrderNumber(orderData?.order_number || null)}
                      </span>
                    </div>

                    {orderData?.total_amount && (
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-sm text-muted-foreground">סכום שחויב:</span>
                        <span className="font-bold text-lg text-success">
                          ₪{orderData.total_amount.toLocaleString()}
                        </span>
                      </div>
                    )}

                    {orderStatus && (
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-sm text-muted-foreground">סטטוס: </span>
                        <span className="font-medium">{getStatusLabel(orderStatus as any)}</span>
                      </div>
                    )}

                    {orderData?.created_at && (
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-sm text-muted-foreground">תאריך הזמנה:</span>
                        <span className="text-sm">
                          {new Date(orderData.created_at).toLocaleDateString('he-IL')} בשעה {' '}
                          {new Date(orderData.created_at).toLocaleTimeString('he-IL', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  {user && (
                    <Button asChild disabled={isProcessing}>
                      <Link to="/profile?tab=orders">
                        הזמנות שלי
                      </Link>
                    </Button>
                  )}
                  <Button variant="outline" asChild disabled={isProcessing}>
                    <Link to="/">
                      חזור לעמוד הבית
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Payment Status Details - only show for non-Invoice4U payments */}
            {transactionId && !orderId && (
              <PaymentStatus
                transactionId={transactionId}
                autoRefresh={true}
              />
            )}


          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default PaymentSuccess;
